import React, { useMemo } from 'react';
import { Box, Button, Flex, Text, Divider } from '@mantine/core';
import {
    IconAlertTriangle,
    IconCircleCheck,
    IconRefresh,
} from '@tabler/icons-react';

import { CurrentPrices } from '../../../codegen/graphql';
import { calculateMargin } from '../../../helpers/calculateMargin';
import { useRequestDrawer } from '../../../context/RequestDrawerContext';
import { useAppConfig } from '../../../hooks/useAppConfig';

type Props = {
    costPrice: number | null;
    currentSellingPrice: number;
    proposedPrice: number;
    benchmarkMargin: number;
    currency: string;
    completed?: boolean | null;
    previousCostPrice?: number | null;
    previousSellingPrice?: number | null;
    previousCashbuildPrice?: number | null;
    previousPnlPrice?: number | null;
    priceType?: 'cashbuild' | 'pnl';
    currentPrices?: CurrentPrices;
    layout?: 'column' | 'row';
    vatPercentage?: number | null;
};

function MarginCalculation({
    costPrice,
    currentSellingPrice,
    proposedPrice,
    benchmarkMargin,
    currency,
    completed,
    previousCostPrice,
    previousSellingPrice,
    previousCashbuildPrice,
    previousPnlPrice,
    priceType,
    currentPrices,
    layout = 'column',
    vatPercentage,
}: Props) {
    const { hasSharedDataError, retrySharedQueries } = useRequestDrawer();
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();
    const resolvedVatPercentage = vatPercentage ?? fallbackVatPercentage;

    const marginDetails = useMemo(() => {
        let resolvedCostPrice = costPrice ?? 0;
        let currentPrice = currentSellingPrice;
        const resolvedProposedPrice = Number(proposedPrice || 0);

        if (priceType === 'pnl') {
            currentPrice =
                previousPnlPrice ??
                currentPrices?.pnlPrice ??
                previousSellingPrice ??
                currentPrice;
        } else if (priceType === 'cashbuild') {
            currentPrice =
                previousCashbuildPrice ??
                currentPrices?.cashbuildPrice ??
                previousSellingPrice ??
                currentPrice;
        } else if (completed && previousSellingPrice) {
            currentPrice = previousSellingPrice;
        }

        if (completed && previousCostPrice) {
            resolvedCostPrice = previousCostPrice;
        }

        return calculateMargin({
            costPrice: resolvedCostPrice,
            currentPrice,
            proposedPrice: resolvedProposedPrice,
            benchmarkMargin: benchmarkMargin || 1,
            vatPercentage: resolvedVatPercentage,
        });
    }, [
        costPrice,
        currentSellingPrice,
        proposedPrice,
        benchmarkMargin,
        completed,
        previousCostPrice,
        previousSellingPrice,
        previousCashbuildPrice,
        previousPnlPrice,
        priceType,
        currentPrices,
        resolvedVatPercentage,
    ]);

    const displayCurrentPrice = useMemo(() => {
        if (priceType === 'pnl') {
            return (
                previousPnlPrice ??
                currentPrices?.pnlPrice ??
                previousSellingPrice ??
                currentSellingPrice ??
                0
            );
        }
        if (priceType === 'cashbuild') {
            return (
                previousCashbuildPrice ??
                currentPrices?.cashbuildPrice ??
                previousSellingPrice ??
                currentSellingPrice ??
                0
            );
        }
        return completed && previousSellingPrice != null
            ? previousSellingPrice
            : currentSellingPrice ?? 0;
    }, [
        currentSellingPrice,
        previousSellingPrice,
        previousCashbuildPrice,
        previousPnlPrice,
        completed,
        priceType,
        currentPrices,
    ]);

    if (hasSharedDataError) {
        return (
            <Box
                bg="#f4f5f7"
                p="md"
                style={{
                    borderRadius: '8px',
                    marginTop: '8px',
                    marginBottom: '8px',
                }}
            >
                <div className="flex flex-col items-center justify-center gap-2 py-2">
                    <Text size="xs" c="dimmed">
                        Failed to load price data.
                    </Text>
                    <Button
                        variant="light"
                        size="compact-xs"
                        leftSection={<IconRefresh size={12} />}
                        onClick={retrySharedQueries}
                    >
                        Retry
                    </Button>
                </div>
            </Box>
        );
    }

    const displayCostPrice = costPrice ?? 0;

    const hasValidMargin =
        proposedPrice != null &&
        proposedPrice > 0 &&
        Number.isFinite(marginDetails.newMargin);

    const proposedMarginDisplay = hasValidMargin ? (
        <div className="flex items-center gap-1">
            {marginDetails.isMarginPositive ? (
                <IconCircleCheck className="text-green-500" size={16} />
            ) : (
                <IconAlertTriangle className="text-amber-500" size={16} />
            )}
            <Text
                className={`font-bold text-sm ${
                    marginDetails.isMarginPositive
                        ? 'text-green-500'
                        : 'text-amber-600'
                }`}
            >
                {parseFloat(`${marginDetails.newMargin}`).toFixed(2)}%
            </Text>
        </div>
    ) : (
        <Text className="font-bold text-sm text-[#7D8FB3]">—</Text>
    );

    const rowColumnClassName = 'shrink-0 p-1';
    const labelClassName = `uppercase text-[11px] text-[#7D8FB3] font-bold leading-tight ${
        layout === 'row' ? 'whitespace-nowrap' : ''
    }`;

    return (
        <Box
            bg="#f4f5f7"
            p="md"
            style={{
                borderRadius: '8px',
                marginTop: '8px',
                marginBottom: '8px',
            }}
        >
            <Flex
                direction={layout}
                gap={layout === 'row' ? 0 : 'xs'}
                justify={layout === 'row' ? 'space-between' : undefined}
                align={layout === 'row' ? 'flex-start' : undefined}
                wrap={layout === 'row' ? 'nowrap' : undefined}
                className={layout === 'row' ? 'w-full' : undefined}
            >
                <div className={layout === 'row' ? rowColumnClassName : ''}>
                    <Text className={labelClassName}>CURRENT PRICE</Text>
                    <Text className="font-bold text-primary text-sm">
                        {currency} {displayCurrentPrice.toFixed(2)}
                    </Text>
                </div>

                {layout === 'row' ? (
                    <Divider orientation="vertical" color="#e6e9ef" my="xs" />
                ) : (
                    <Box
                        style={{
                            height: '1px',
                            backgroundColor: '#e0e0e0',
                            margin: '8px 0',
                        }}
                    />
                )}

                <div className={layout === 'row' ? rowColumnClassName : ''}>
                    <Text className={labelClassName}>COST PRICE</Text>
                    <Text className="font-bold text-primary text-sm">
                        {currency} {displayCostPrice.toFixed(2)}
                    </Text>
                </div>

                {layout === 'row' ? (
                    <Divider orientation="vertical" color="#e6e9ef" my="xs" />
                ) : (
                    <Box
                        style={{
                            height: '1px',
                            backgroundColor: '#e0e0e0',
                            margin: '8px 0',
                        }}
                    />
                )}

                <div className={layout === 'row' ? rowColumnClassName : ''}>
                    <Text className={labelClassName}>PROPOSED MARGIN</Text>
                    {proposedMarginDisplay}
                </div>

                {layout === 'row' ? (
                    <Divider orientation="vertical" color="#e6e9ef" my="xs" />
                ) : (
                    <Box
                        style={{
                            height: '1px',
                            backgroundColor: '#e0e0e0',
                            margin: '8px 0',
                        }}
                    />
                )}

                <div className={layout === 'row' ? rowColumnClassName : ''}>
                    <Text className={labelClassName}>BENCHMARK MARGIN</Text>
                    <Text className="font-bold text-primary text-sm">
                        {parseFloat(
                            (benchmarkMargin || '0').toString(),
                        ).toFixed(2)}
                        %
                    </Text>
                </div>
            </Flex>
        </Box>
    );
}

export default MarginCalculation;
